import { type JSX, useState } from 'react';
import { Link, useLocation } from 'react-router';

import { ChevronRight, MoreHorizontal, Plus, Search, X } from 'lucide-react';
import { Collapsible } from 'radix-ui';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  useSidebar,
} from '@/components/ui/sidebar';

import { type SubProjectNavigationItem, useSubProjectSearch } from '../model/subProjectSearch';
import SubProjectsMenu from './SubProjectsMenu';

interface Props {
  selectedSubProjects: SubProjectNavigationItem[];
  projectBasePath: string;
}

const SubProjectsNav = ({ selectedSubProjects, projectBasePath }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const { state: sidebarState } = useSidebar();
  const isSidebarCollapsed = sidebarState === 'collapsed';
  const [isOpen, setIsOpen] = useState(true);
  const { clearSearch, filteredSubProjects, searchQuery, setSearchQuery } = useSubProjectSearch(
    selectedSubProjects,
    projectBasePath
  );

  return (
    <Collapsible.Root asChild open={isSidebarCollapsed || isOpen} onOpenChange={setIsOpen} className="group/projects">
      <SidebarGroup className="shrink-0">
        <Collapsible.Trigger asChild>
          <SidebarGroupLabel
            asChild
            className="group-data-[collapsible=icon]:hidden text-white/50 uppercase font-body text-[11px] font-medium tracking-wider cursor-pointer hover:text-white/80"
          >
            <button type="button">
              <span className="min-w-0 flex-1 truncate text-left pr-7">Proyectos</span>
              <ChevronRight className="ml-auto shrink-0 transition-transform group-data-[state=open]/projects:rotate-90" />
            </button>
          </SidebarGroupLabel>
        </Collapsible.Trigger>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarGroupAction aria-label="Acciones de proyectos" className="right-9">
              <MoreHorizontal />
            </SidebarGroupAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start">
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={`${projectBasePath}/createSubProject`}>
                  <Plus />
                  <span>Crear Subproyecto</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Collapsible.Content>
          <SidebarGroupContent>
            <div className="mb-2 flex items-center gap-2 px-2 group-data-[collapsible=icon]:hidden">
              <div className="relative min-w-0 flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-white/45" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Buscar subproyectos"
                  className="h-9 border-white/20 bg-white/5 pl-9 pr-10 text-sm text-white placeholder:text-white/40 focus-visible:border-brand-vivid focus-visible:ring-brand-vivid/30"
                />
                {searchQuery && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    onClick={clearSearch}
                    className="absolute top-1/2 right-1 -translate-y-1/2 text-white/60 hover:bg-white/10 hover:text-white"
                    aria-label="Limpiar búsqueda"
                  >
                    <X />
                  </Button>
                )}
              </div>
            </div>
            <SubProjectsMenu
              subProjects={filteredSubProjects}
              projectBasePath={projectBasePath}
              pathname={pathname}
              searchQuery={searchQuery}
            />
            {searchQuery.trim() && filteredSubProjects.length === 0 && (
              <p className="p-2 text-sm text-white/45">No hay subproyectos que coincidan con la búsqueda.</p>
            )}
          </SidebarGroupContent>
        </Collapsible.Content>
      </SidebarGroup>
    </Collapsible.Root>
  );
};

export default SubProjectsNav;
